// Компактные подписи для карточек сегментов.
// Переводчик передаётся параметром: core не знает про React-контекст, а без
// явного аргумента подписи молча остались бы на одном языке.
import type { CurrencyDef, Segment, SegmentKind } from '../types';
import { createTranslator, type Translate } from '../i18n/translations';
import { formatMoney } from './money';
import { formatDuration, formatLocal } from './time';

/** Язык по умолчанию для вызовов вне UI (тесты, серверные утилиты). */
const defaultT: Translate = createTranslator('ru');

/** «1 ночь» / «2 ночи» / «5 ночей» — форма выбирается по правилам языка. */
export function pluralNights(n: number, t: Translate = defaultT): string {
  return t('summary.nights', { count: n });
}

export function segmentTitle(s: Segment, t: Translate = defaultT): string {
  switch (s.kind) {
    case 'flight':
      return `${s.from} → ${s.to}`;
    case 'transfer':
      return `${t('kind.transfer')}: ${s.from} → ${s.to}`;
    case 'lodging':
      // «На весь промежуток» ночей в подписи нет: их число зависит от места
      // блока в варианте и считается в calc, а не хранится.
      if (s.wholeStay) return `${s.city} · ${t('labels.wholeStay')}`;
      // Город — произвольная строка, склонять его нельзя, поэтому он идёт
      // отдельным словом: «Ереван · 2 ночи», а не «Ночь в Ереван ×2».
      return `${s.city} · ${pluralNights(s.nights, t)}`;
    case 'taxi':
    case 'food':
    case 'misc':
      return s.label;
    case 'baggage':
      return s.label;
  }
}

export function segmentSubtitle(
  s: Segment,
  currencies: readonly CurrencyDef[],
  t: Translate = defaultT,
): string {
  switch (s.kind) {
    case 'flight': {
      // Перевозчик необязателен: без него подпись начинается сразу со времени,
      // а не с осиротевшего разделителя.
      const num = s.flightNumber ? ` ${s.flightNumber}` : '';
      const who = `${s.carrier}${num}`.trim();
      return who ? `${who} · ${formatLocal(s.departLocal)}` : formatLocal(s.departLocal);
    }
    case 'transfer': {
      const per = s.perPerson ? ` · ${t('field.perPerson').toLowerCase()}` : '';
      return `${formatDuration(s.durationMin, t)}${per}`;
    }
    case 'lodging': {
      const per = s.perPerson ? ` · ${t('field.perPerson').toLowerCase()}` : '';
      return `${formatMoney(s.pricePerNight.amount, s.pricePerNight.currency, currencies)}${t('labels.perNight')}${per}`;
    }
    case 'taxi':
    case 'food':
    case 'misc': {
      const price = `${s.quantity}×${formatMoney(s.price.amount, s.price.currency, currencies)}`;
      return s.city ? `${s.city} · ${price}` : price;
    }
    case 'baggage':
      return formatMoney(s.price.amount, s.price.currency, currencies);
  }
}

export function segmentAccent(kind: SegmentKind): 'flight' | 'transfer' | 'lodging' | 'cost' {
  switch (kind) {
    case 'flight':
      return 'flight';
    case 'transfer':
      return 'transfer';
    case 'lodging':
      return 'lodging';
    case 'taxi':
    case 'food':
    case 'misc':
    case 'baggage':
      return 'cost';
  }
}
